import { QuizService } from '@repo/api/quiz/quizService';
import { ErrorMessageHandler } from '@repo/lib/errorMessageHandler';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import SelectFsItemsDialog from '@/components/features/files/select-fs-items/SelectFsItemsDialog';
import { getFileUrl } from '@/utils/file';

/**
 * Props for the AnswerMediaSelectDialog component.
 * @property open - Controls dialog visibility.
 * @property onOpenChange - Callback for dialog open state changes.
 * @property accept - Accepted file types.
 * @property contentChange - Callback for content change after file selection.
 */
interface AnswerMediaSelectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accept?: string;
  contentChange?: (content: string) => void;
}

export function AnswerMediaSelectDialog(answerMediaSelectDialogProps: AnswerMediaSelectDialogProps) {
  const { open, onOpenChange, contentChange, accept = 'image/*,video/*,audio/*' } = answerMediaSelectDialogProps;

  const { quizId } = useParams();

  return (
    <SelectFsItemsDialog
      open={open}
      onOpenChange={onOpenChange}
      options={{
        multiple: false,
        maxFiles: 1,
        allow: {
          file: true,
          directory: false,
        },
        accept: accept,
        maxSize: 1000000 * 15,
        uploadFileFn: async (file) => {
          if (!quizId) return;
          await QuizService.uploadQuizMedia(quizId, file)
            .then((response) => {
              const type: string | undefined = response.data.fileType.split('/')[0];
              const mediaUrl: string = getFileUrl(response.data.id);
              contentChange?.(`[${type}:](${mediaUrl})`);
            })
            .catch((error) => {
              const errorHandler = new ErrorMessageHandler(baseErrorMessages);
              const errorMessage = errorHandler.processError(error);

              toast.error(`Błąd podczas przesyłania pliku: ${file.name}`, {
                description: errorMessage.detail,
              });
            });
        },
        onFileUploadError: (file: File, _error, issue) => {
          if (issue.code === 'FILE_TOO_LARGE') {
            toast.error(`Plik ${file.name} jest zbyt duży i nie został dodany do listy przesyłania.`);
          } else if (issue.code === 'UPLOAD_FAILED') {
            toast.error(`Plik ${file.name} nie został przesłany.`);
          }
        },
        selectedCallbackFn: async (selected) => {
          // Handle selected media
          const selectedFile = selected?.files[0];
          if (!selectedFile) return;
          if (!quizId) return;
          await QuizService.uploadQuizMedia(quizId, selectedFile.id)
            .then((response) => {
              const type: string | undefined = response.data.fileType.split('/')[0];
              const mediaUrl: string = getFileUrl(response.data.id);
              contentChange?.(`[${type}:](${mediaUrl})`);
            })
            .catch((error) => {
              const errorHandler = new ErrorMessageHandler(baseErrorMessages);
              const errorMessage = errorHandler.processError(error);
              toast.error(`Błąd podczas przesyłania pliku: ${selectedFile.name}`, {
                description: errorMessage.detail,
              });
            });
        },
      }}
    />
  );
}
